import React, { useEffect } from 'react';
import { Col, Form, Row, Select as AntSelect, Skeleton } from 'antd';
import { I18n } from '@aws-amplify/core';
import { v4 as uuidv4 } from 'uuid';

import type { AdditionalItemsResults, GetAdditionalToolScore } from '@/hooks';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useStepKeysContext } from '../DefaultSelects/context';
import { useCalculateAdditionalToolScore, useGetAdditionalItemsOptions } from '@/hooks';
import { fieldsUnitTexts } from './utils/FieldsUnitTexts';
import { ConditionCard } from '../../ConditionCard';
import { Text } from '@/components/Typography';
import { AditionalItemProps, Options } from './types';

const { useWatch, useFormInstance } = Form;
const { formatLabelWithUnit } = fieldsUnitTexts;

export function Select({ additionalItem }: Readonly<AditionalItemProps>): JSX.Element {
	const { id, title_1, unit_1 } = additionalItem;

	const form = useFormInstance();
	const { formFieldName, stepKey } = useStepKeysContext();
	const { organization, company } = useApplicationContext();

	const selectedOptionId = useWatch([...formFieldName, 'additional_items', id, 'additional_item_option_id_1']);

	const {
		data: options,
		isLoading: isGettingOptions,
		isFetching: isFetchingOptions
	} = useGetAdditionalItemsOptions({
		company_id: company?.id,
		organization_id: organization?.id,
		custom_report_step_key_id: stepKey.id
	});

	const payload: GetAdditionalToolScore = {
		company_id: company?.id,
		organization_id: organization?.id,
		custom_report_step_key_id: stepKey.id,
		additional_item_option_id_1: selectedOptionId
	};

	const { data: additionalToolScore, isFetching: isFetchingScore } = useCalculateAdditionalToolScore(payload);

	const selectLabel = formatLabelWithUnit(title_1, unit_1);

	useEffect(() => {
		const result: AdditionalItemsResults[] = [
			{
				id: uuidv4(),
				additional_item_option_id_1: selectedOptionId,
				result: additionalToolScore?.result ?? 0
			}
		];

		form.setFieldValue([...formFieldName, 'additional_items', id, 'results'], result);
	}, [additionalToolScore]);

	function filterOption(input: string, option?: Options): boolean {
		return (option?.label.toLowerCase() ?? '').includes(input.toLowerCase());
	}

	if (isGettingOptions) {
		return (
			<Row gutter={[0, 15]}>
				<Col span={18}>
					<Skeleton active />
				</Col>
				<Col span={22}>
					<Skeleton active paragraph />
				</Col>
			</Row>
		);
	}

	const selectOptions = options.map(({ id, description }) => ({
		value: id,
		label: I18n.get(`${description}`)
	}));

	return (
		<Row gutter={[0, 15]} key={id}>
			<Col span={18}>
				<Text strong>{selectLabel}</Text>
			</Col>
			<Col span={16}>
				<Form.Item name={[...formFieldName, 'additional_items', id, 'additional_item_option_id_1']}>
					<AntSelect
						showSearch
						allowClear
						filterOption={filterOption}
						placeholder={I18n.get(title_1)}
						options={selectOptions}
					/>
				</Form.Item>
			</Col>
			<Col span={22}>
				<ConditionCard
					result={selectedOptionId ? additionalToolScore?.result : 0}
					isLoading={isGettingOptions || isFetchingOptions || isFetchingScore}
				/>
			</Col>
		</Row>
	);
}
